/** Seed derivation, weighted picks and shareable seed codes for the galaxy generator (M3, D-033). */
import { nextFloat, type Rng } from '../core/rng'

const FNV_OFFSET = 0x811c9dc5
const FNV_PRIME = 0x01000193
const PART_SEPARATOR = 0x1f

function fnvByte(h: number, byte: number): number {
  return Math.imul(h ^ byte, FNV_PRIME) >>> 0
}

function fnvString(h: number, s: string): number {
  let out = h
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    out = fnvByte(out, c & 0xff)
    out = fnvByte(out, c >>> 8)
  }
  return out
}

/** murmur3 fmix32: spreads FNV's weak low bits over the whole word. */
function mix32(h: number): number {
  let x = h
  x ^= x >>> 16
  x = Math.imul(x, 0x85ebca6b)
  x ^= x >>> 13
  x = Math.imul(x, 0xc2b2ae35)
  x ^= x >>> 16
  return x >>> 0
}

/**
 * Deterministically hashes a seed plus labelled parts into a new uint32 seed (FNV-1a, then mixed).
 * Numbers and strings hash differently (1 ≠ '1'), and part boundaries matter ('ab','c' ≠ 'a','bc').
 */
export function deriveSeed(seed: number, ...parts: (string | number)[]): number {
  const s = seed >>> 0
  let h = FNV_OFFSET
  h = fnvByte(h, s & 0xff)
  h = fnvByte(h, (s >>> 8) & 0xff)
  h = fnvByte(h, (s >>> 16) & 0xff)
  h = fnvByte(h, s >>> 24)
  for (const part of parts) {
    h = fnvByte(h, PART_SEPARATOR)
    h = fnvString(h, typeof part === 'number' ? `#${part}` : `$${part}`)
  }
  return mix32(h)
}

/** Picks a key with probability proportional to its weight. Keys are walked in insertion order. */
export function pickWeighted<T extends string>(rng: Rng, weights: Record<T, number>): T {
  const keys = Object.keys(weights) as T[]
  let total = 0
  for (const k of keys) if (weights[k] > 0) total += weights[k]
  if (!(total > 0)) throw new Error('pickWeighted: no positive weight')
  let r = nextFloat(rng) * total
  let last: T = keys[0]
  for (const k of keys) {
    const w = weights[k]
    if (!(w > 0)) continue
    last = k
    if (r < w) return k
    r -= w
  }
  return last // float round-off guard
}

/** A uniformly distributed integer in [min, max], both inclusive. */
export function intRange(rng: Rng, min: number, max: number): number {
  return min + Math.floor(nextFloat(rng) * (max - min + 1))
}

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
/** 7 chars × 5 bits = 35 bits ≥ 32. Six chars only hold 30 bits, so they cannot round-trip a uint32. */
export const SEED_CODE_LENGTH = 7

/** Encodes a uint32 seed as a 7-character Crockford base32 code, most significant digit first. */
export function encodeSeed(n: number): string {
  let v = n >>> 0
  let out = ''
  for (let i = 0; i < SEED_CODE_LENGTH; i++) {
    out = CROCKFORD[v % 32] + out
    v = Math.floor(v / 32)
  }
  return out
}

function crockfordValue(ch: string): number {
  const c = ch.toUpperCase()
  if (c === 'O') return 0
  if (c === 'I' || c === 'L') return 1
  return CROCKFORD.indexOf(c)
}

/** Decodes a seed code: case-insensitive, hyphens ignored, Crockford aliases (O→0, I/L→1). Null if invalid. */
export function decodeSeed(code: string): number | null {
  const clean = code.replaceAll('-', '')
  if (clean.length !== SEED_CODE_LENGTH) return null
  let v = 0
  for (const ch of clean) {
    const d = crockfordValue(ch)
    if (d < 0) return null
    v = v * 32 + d
  }
  return v <= 0xffffffff ? v : null
}

/** Reads user input as a seed: a 7-character code first, otherwise a decimal uint32. Null if neither. */
export function parseSeedText(text: string): number | null {
  const t = text.trim()
  const fromCode = decodeSeed(t)
  if (fromCode !== null) return fromCode
  if (!/^\d{1,10}$/.test(t)) return null
  const n = Number(t)
  return n <= 0xffffffff ? n : null
}
